// The countdown widget's arithmetic, apart from the page so it can be checked on its own (it uses no DOM): the
// formats (how many groups a span needs), the time left in those units (calendar months and years), and readTarget(),
// which reads what is typed into the widget's Edit field as an end time.
export const SEC = 1000, MIN = 60 * SEC, HOUR = 60 * MIN, DAY = 24 * HOUR;
// The groups shown, by the countdown's span: each entry's first unit takes everything above it (90 minutes in an
// hour-long format reads 90:00, never 1:30:00, since the span decides the format).
export const FORMATS = [
  { below: HOUR, units: ['m', 's'] },
  { below: DAY, units: ['h', 'm', 's'] },
  { below: 30 * DAY, units: ['d', 'h', 'm', 's'] },
  { below: 365 * DAY, units: ['mo', 'd', 'h', 'm', 's'] },
  { below: Infinity, units: ['y', 'mo', 'd', 'h', 'm', 's'] },
];
const UNIT_NAMES = { y: 'year', mo: 'month', d: 'day', h: 'hour', m: 'minute', s: 'second' };

// `date` plus whole months, kept on the same day of the month where it exists (Jan 31 + 1 month is Feb 28 or 29).
export function addMonths(date, n) {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
  return d;
}
// `date` plus whole days on the calendar (the same clock time, whatever the clock changes in between), in ms.
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d.getTime(); };
// The time from now to `end` in the units of a format, counted in whole seconds rounded up (so the last second shows
// 00:01 and the end shows 00:00). Months and years are calendar ones from now; the first unit takes all above it.
export function remaining(end, units, now = Date.now()) {
  const secs = Math.max(0, Math.ceil((end - now) / SEC));
  const from = new Date(end - secs * SEC); // now, on a whole second before the end
  const out = {};
  let rest = secs * SEC;
  if (units.includes('mo')) {
    let months = (new Date(end).getFullYear() - from.getFullYear()) * 12 + new Date(end).getMonth() - from.getMonth();
    while (months > 0 && addMonths(from, months) > new Date(end)) months--;
    rest = end - addMonths(from, months).getTime();
    if (units.includes('y')) { out.y = Math.floor(months / 12); out.mo = months % 12; } else out.mo = months;
  }
  if (units.includes('d')) {
    // calendar days, so a day across a clock change (23 or 25 hours) still counts as one and the hours stay right
    const cursor = new Date(end - rest);
    let days = Math.floor(rest / DAY);
    while (addDays(cursor, days + 1) <= end) days++;
    while (days > 0 && addDays(cursor, days) > end) days--;
    out.d = days;
    rest = end - addDays(cursor, days);
  }
  for (const [unit, ms] of [['h', HOUR], ['m', MIN], ['s', SEC]]) {
    if (!units.includes(unit)) continue;
    out[unit] = Math.floor(rest / ms);
    rest -= out[unit] * ms;
  }
  return { secs, parts: units.map((u) => out[u] ?? 0) };
}
export const spelled = (units, parts) => units.map((u, i) => parts[i] && `${parts[i]} ${UNIT_NAMES[u]}${parts[i] === 1 ? '' : 's'}`)
  .filter(Boolean).join(', ');


// ---- reading what is typed into Edit: the end as ms, or null when it cannot be read
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DURATION_UNITS = [
  [/^(y|yrs?|years?)$/, 'y'], [/^(mo|mos|months?)$/, 'mo'], [/^(w|wks?|weeks?)$/, 'w'], [/^(d|days?)$/, 'd'],
  [/^(h|hrs?|hours?)$/, 'h'], [/^(m|mins?|minutes?)$/, 'm'], [/^(s|secs?|seconds?)$/, 's'],
];
const UNIT_MS = { w: 7 * DAY, d: DAY, h: HOUR, m: MIN, s: SEC };
const CALENDAR_DAYS = { w: 7, d: 1 }; // whole days and weeks move the date and keep the clock time

// "5m", "1h 30m", "1h30m", "2 days", "90 seconds", "1y 2mo"; a bare number is minutes.
function readDuration(text, now) {
  if (/^\d+(\.\d+)?$/.test(text)) return now + Number(text) * MIN;
  const pieces = [...text.matchAll(/(\d+(?:\.\d+)?)\s*([a-z]+)/g)];
  if (!pieces.length || pieces.map((p) => p[0]).join('').replace(/\s/g, '') !== text.replace(/[\s,]|and/g, '')) return null;
  let at = new Date(now);
  let ms = 0;
  for (const [, n, word] of pieces) {
    const unit = DURATION_UNITS.find(([re]) => re.test(word))?.[1];
    if (!unit) return null;
    if (unit === 'y' || unit === 'mo') {
      if (!Number.isInteger(Number(n))) return null;
      at = addMonths(at, Number(n) * (unit === 'y' ? 12 : 1));
    } else if (CALENDAR_DAYS[unit] && Number.isInteger(Number(n))) at = new Date(addDays(at, Number(n) * CALENDAR_DAYS[unit]));
    else ms += Number(n) * UNIT_MS[unit];
  }
  return at.getTime() + ms;
}
// "05:00" (mm:ss), "1:30:00" (h:mm:ss), "2:01:30:00" (d:hh:mm:ss): a duration.
function readClockDuration(text, now) {
  if (!/^\d+(:\d{1,2}){1,3}$/.test(text)) return null;
  const nums = text.split(':').map(Number).reverse();
  return now + [SEC, MIN, HOUR, DAY].reduce((sum, ms, i) => sum + (nums[i] ?? 0) * ms, 0);
}
// A time of day: "3pm", "3:30 pm", "3a", "noon", "midnight", or 24-hour after "at" ("at 15:30"). Answers [hours, minutes].
function readTime(text, allow24) {
  if (text === 'noon') return [12, 0];
  if (text === 'midnight') return [0, 0];
  let m = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)$/);
  if (m) {
    const h = Number(m[1]), min = Number(m[2] ?? 0);
    if (h < 1 || h > 12 || min > 59) return null;
    return [(h % 12) + (m[3].startsWith('p') ? 12 : 0), min];
  }
  m = allow24 && text.match(/^(\d{1,2}):(\d{2})$/);
  if (m && Number(m[1]) < 24 && Number(m[2]) < 60) return [Number(m[1]), Number(m[2])];
  return null;
}
const at = (date, [h, m] = [0, 0]) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m).getTime();
// A day, maybe with a time after it (null when it reads as a day that cannot be, undefined when it is not written as
// one at all): "tomorrow", "friday 5pm", "July 15 2028", "jul 15th, 2028 at 9am", "15 July",
// "7/15/2028", "2028-07-15 09:00". Without a year, the next time that date comes; without a time, its midnight.
function readDate(text, now) {
  const today = new Date(now);
  const split = (rest) => {
    rest = rest.replace(/^,?\s*(at\s+)?/, '').trim();
    if (!rest) return [0, 0];
    return readTime(rest, true);
  };
  let m = text.match(/^(today|tonight|tomorrow)\b(.*)$/);
  if (m) {
    const time = split(m[2]);
    if (!time) return undefined;
    const day = new Date(now);
    if (m[1] === 'tomorrow') day.setDate(day.getDate() + 1);
    return at(day, m[2].trim() ? time : m[1] === 'tonight' ? [21, 0] : [0, 0]);
  }
  m = text.match(/^([a-z]+)\b(.*)$/);
  const weekday = m && WEEKDAYS.indexOf(m[1].slice(0, 3));
  if (m && weekday >= 0 && /^(sun|mon|tue|wed|thu|fri|sat)[a-z]*$/.test(m[1])) {
    const time = split(m[2]);
    if (!time) return undefined;
    const day = new Date(now);
    day.setDate(day.getDate() + ((weekday - day.getDay() + 7) % 7));
    if (at(day, time) <= now) day.setDate(day.getDate() + 7);
    return at(day, time);
  }
  let y, mo, d, rest;
  if ((m = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[t\s]+(.*))?$/))) [y, mo, d, rest] = [+m[1], +m[2] - 1, +m[3], m[4] ?? ''];
  else if ((m = text.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b(.*)$/))) [mo, d, y, rest] = [+m[1] - 1, +m[2], m[3] && (+m[3] < 100 ? 2000 + +m[3] : +m[3]), m[4]];
  else if ((m = text.match(/^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b(.*)$/))) [mo, d, y, rest] = [MONTHS.indexOf(m[1].slice(0, 3)), +m[2], m[3] && +m[3], m[4]];
  else if ((m = text.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?(?:,?\s+(\d{4}))?\b(.*)$/))) [d, mo, y, rest] = [+m[1], MONTHS.indexOf(m[2].slice(0, 3)), m[3] && +m[3], m[4]];
  else return undefined; // not a date written in any of these ways
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  const time = split(rest ?? '');
  if (!time) return undefined; // a time written some other way: left to the browser's reading
  const make = (year) => {
    const date = new Date(year, mo, d, ...time);
    return date.getMonth() === mo ? date.getTime() : null; // no Feb 30
  };
  if (y) return make(y);
  const thisYear = make(today.getFullYear());
  return thisYear && thisYear > now ? thisYear : make(today.getFullYear() + 1);
}
// What Edit's field reads as an end, or null. Tried in turn: a time of day (today, or tomorrow when it has passed), a
// duration, mm:ss, a day or date; then anything else the browser can read as a date (an ISO time with a zone, say).
// Holidays on a set date: midnight starting the day, local time; the coming one (today's has begun, so next year's),
// or that of a year given ("christmas 2030"). New Year's Eve counts to the midnight that ends it (December 32nd, which
// Date rolls over to January 1st), the moment it is about, so "nye 2030" is the one ending 2030. Holidays whose date moves (Easter, Thanksgiving, Diwali) are left out: type their date.
const HOLIDAYS = [
  ["New Year's Eve", /new ?year(?:'?s)? eve|nye/, 11, 32],
  ['New Year', /new ?year(?:'?s)?(?: day)?/, 0, 1],
  ['Groundhog Day', /groundhog(?:'?s)? day/, 1, 2],
  ["Valentine's Day", /(?:st\.? )?valentine(?:'?s)?(?: day)?/, 1, 14],
  ["St. Patrick's Day", /(?:st\.? |saint )?patrick(?:'?s)?(?: day)?|st\.? paddy(?:'?s)?(?: day)?/, 2, 17],
  ["April Fools' Day", /april fool(?:s'|'?s)?(?: day)?/, 3, 1],
  ['Earth Day', /earth day/, 3, 22],
  ['Cinco de Mayo', /cinco de mayo/, 4, 5],
  ['Juneteenth', /juneteenth/, 5, 19],
  ['Canada Day', /canada day/, 6, 1],
  ['Independence Day', /independence day|(?:the )?(?:4th|fourth) of july|july (?:4th|fourth)/, 6, 4],
  ['Bastille Day', /bastille day/, 6, 14],
  ['Halloween', /hallowe'?en/, 9, 31],
  ["All Saints' Day", /all saints(?:'? day)?/, 10, 1],
  ['Día de los Muertos', /dia de (?:los )?muertos|day of the dead/, 10, 2],
  ['Veterans Day', /veterans(?:'? day)?/, 10, 11],
  ['Remembrance Day', /remembrance day|armistice day/, 10, 11],
  ['Christmas Eve', /christmas eve|xmas eve/, 11, 24],
  ['Christmas', /christmas(?: day)?|xmas(?: day)?/, 11, 25],
  ['Boxing Day', /boxing day/, 11, 26],
].map(([name, re, month, day]) => [name, new RegExp(`^(?:the )?(?:${re.source})(?: (\\d{4}))?$`), month, day]);
// Lower case, single spaces, straight apostrophes and no accents (día reads as dia).
const clean = (input) => String(input).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ').replace(/[’]/g, "'");
// Every holiday by name, the coming ones first, with when each begins (the Edit field's suggestions).
export function holidays(now = Date.now()) {
  return HOLIDAYS.map(([name]) => ({ name, end: readHoliday(name, now).end })).sort((a, b) => a.end - b.end);
}
// The holiday typed, if it is one: its name (for the caption) and when it begins.
export function readHoliday(input, now = Date.now()) {
  const text = clean(input);
  for (const [name, re, month, day] of HOLIDAYS) {
    const m = re.exec(text);
    if (!m) continue;
    if (m[1]) return { name, end: new Date(Number(m[1]), month, day).getTime() };
    const year = new Date(now).getFullYear();
    const thisYear = new Date(year, month, day).getTime();
    return { name, end: thisYear > now ? thisYear : new Date(year + 1, month, day).getTime() };
  }
  return null;
}

export function readTarget(input, now = Date.now()) {
  const text = clean(input);
  if (!text) return null;
  const holiday = readHoliday(text, now);
  if (holiday) return holiday.end;
  const time = readTime(text.replace(/^at /, ''), text.startsWith('at '));
  if (time) {
    let end = at(new Date(now), time);
    if (end <= now) end = at(new Date(now + DAY), time);
    return end;
  }
  const end = readDuration(text, now) ?? readClockDuration(text, now);
  if (end != null) return end;
  const date = readDate(text, now);
  if (date !== undefined) return date; // a day it read, or one it rejected (Feb 30): never the browser's lenient guess
  const parsed = Date.parse(input);
  return Number.isFinite(parsed) ? parsed : null;
}
